import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/shared";
import { Combobox } from "@/components/shared/Combobox";

const init = (p, name = "") => ({ name: p?.name || name, department_id: p?.department_id || null, email: p?.email || "", active: p ? p.active : true });

export const PersonDialog = ({ open, onOpenChange, person, initialName = "", onSaved }) => {
  const [f, setF] = useState(init(person, initialName));
  const [depts, setDepts] = useState([]);
  const [dupes, setDupes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(init(person, initialName)); setDupes(null); api.get("/departments").then((r) => setDepts(r.data)); } }, [open, person, initialName]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const addDept = async (name) => {
    const n = (name || window.prompt("New department name"))?.trim();
    if (!n) return;
    try { const { data } = await api.post("/departments", { name: n }); setDepts((d) => (d.some((x) => x.id === data.id) ? d : [...d, data])); setF((x) => ({ ...x, department_id: data.id })); } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async (e, { force = false, another = false } = {}) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, department_id: f.department_id, email: f.email, active: f.active, force };
      const { data } = person ? await api.patch(`/people/${person.id}`, body) : await api.post("/people", body);
      toast.success(person ? "Team member updated" : `${data.name} added to Internal Team`);
      onSaved?.(data);
      if (another) { setF(init(null)); setDupes(null); } else onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === "possible_duplicate") setDupes(d.matches);
      else toast.error(errMsg(err));
    } finally { setBusy(false); }
  };
  const deptItems = depts.filter((d) => d.active || d.id === f.department_id).map((d) => ({ value: d.id, label: d.active ? d.name : `${d.name} (inactive)` }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="person-dialog">
        <DialogHeader>
          <DialogTitle>{person ? "Edit team member" : "Add team member"}</DialogTitle>
          <DialogDescription>Directory record only — this does not create a login or grant access.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <Field label="Full name" required><Input data-testid="person-name-input" value={f.name} onChange={set("name")} required autoFocus /></Field>
          <Field label="Department"><Combobox testid="person-department-select" items={deptItems} value={f.department_id} onChange={set("department_id")} placeholder="Select department" addLabel="Add department" onAdd={addDept} /></Field>
          <Field label="Email"><Input data-testid="person-email-input" type="email" value={f.email} onChange={set("email")} /></Field>
          <Field label="Status">
            <Select value={f.active ? "active" : "inactive"} onValueChange={(v) => set("active")(v === "active")}>
              <SelectTrigger data-testid="person-status-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
            </Select>
          </Field>
          {dupes && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-3" data-testid="person-duplicate-warning">
              <div className="flex items-center gap-1.5 font-medium text-amber-800"><AlertTriangle className="h-3.5 w-3.5" /> Possible duplicate</div>
              <ul className="mt-1 ml-5 list-disc text-amber-900">{dupes.map((d) => <li key={d.id}>{d.name}{d.email ? ` · ${d.email}` : ""}{d.active === false ? " (inactive)" : ""}</li>)}</ul>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {dupes ? <Button type="button" data-testid="person-save-anyway-button" variant="secondary" disabled={busy} onClick={(e) => submit(e, { force: true })}>Save anyway</Button> : (<>
              {!person && <Button type="button" variant="secondary" data-testid="person-save-another-button" disabled={busy || !f.name.trim()} onClick={(e) => submit(e, { another: true })}>Save & Add Another</Button>}
              <Button data-testid="person-submit-button" type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={busy || !f.name.trim()}>{busy ? "Saving…" : person ? "Save changes" : "Add team member"}</Button>
            </>)}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
